import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {DtoEnumInfo} from 'features/core/shared/dto/dto-enum-info.interface';
import {
    ProvisionSimulationCandidateDto,
    ProvisionSimulationEntitlementDto,
    ProvisionSimulationFigureDto,
    ProvisionSimulationMessageDto,
    ProvisionSimulationStepDto,
    ProvisionSimulationVerdictCountDto
} from '../../../model/dto/api/provision-simulation.dto';
import {
    ProvisionSimulationCandidateVerdict
} from '../../../enum/provision-simulation-candidate-verdict.enum';
import {provisionEnumLabelFallback} from '../../../enum/provision-enum-label-fallback';
import {ProvisionSimulationState} from '../../../enum/provision-simulation-state.enum';
import {ProvisionAgentRoleEnum} from '../../../enum/provision-agent-role.enum';
import {ProvisionConfigurationAxis} from '../../../enum/provision-configuration-axis.enum';
import {ProvisionMessageSeverity} from '../../../enum/provision-message-severity.enum';
import {
    formatProvisionSimulationValue
} from '../../../service/domain/cp-simulation-figure-format';
import {
    provisionSimulationChipStyle,
    provisionSimulationResolvedState
} from '../../../service/domain/cp-simulation-presentation';
import {provisionSimulationEnumLabel} from '../../../enum/provision-simulation-technical-label';

/**
 * The RECHENPROTOKOLL of one simulation — the ordered list of steps the calculation chain walked,
 * each with its key date, its verdict, its winner and its candidates WITH the reason each of them
 * did not win.
 *
 * ── THIS COMPONENT IS THE ORDERED PRODUCT
 *
 * The client's order reads: "Die Anwender wollen das haben um die Berechnungen genau testen zu
 * können. **Hier brauchen wir dann detaillierte Informationen wie alles berechnet wurde.**" A single
 * amount does not fill that order. What fills it is the losers: the row somebody EXPECTED to win,
 * beside the sentence saying why it did not. Everything else on the simulation screen serves this
 * list.
 *
 * ── THE ORDER OF THE LIST IS DATA, NOT LAYOUT
 *
 * `steps[]` arrives in EXECUTION order and is rendered in exactly that order; `candidates[]`
 * arrives in the engine's evaluation order (`orderPriority` ascending, id as tie break) and is
 * rendered in exactly that order. Neither is ever sorted, filtered or regrouped here. A re-sorted
 * candidate list silently rewrites the explanation: "SHADOWED — eine Zeile davor hat bereits
 * gewonnen" is only true while "davor" still means what the engine meant. This is why the
 * candidate table has NO sortable column and no default sort, and why the steps are not grouped by
 * entitlement even though every step names the entitlement it belongs to.
 *
 * ── NOTHING IS CALCULATED HERE
 *
 * No sum over figures, no per-mille conversion, no derived winner, no verdict tally. The only
 * numbers this class produces are `steps.length` and `candidates.length` — the size of a delivered
 * list, needed to label a disclosure button, which is not a business figure. Values are FORMATTED
 * for German eyes by {@link formatProvisionSimulationValue} and by nothing else.
 *
 * ── CAPTIONS AND BRANCHES: `…Info` FIRST, THE FE ENUM SECOND, THE RAW STRING NEVER
 *
 * A message's `severity` and a candidate's `axis` arrive as the ORDINAL beside a `severityInfo` /
 * `axisInfo` block with the backend's German label. The caption is read from the block; a response
 * without it (a backend older than the frontend) falls back through the FE enum mirror to the
 * technical-label map. The colour class of a message is decided on the FE enum constant — a raw
 * string is first resolved to that constant and never compared as text. The caption rule lives ONCE,
 * in {@link provisionSimulationEnumLabel}; the branch rule is {@link severityOf}.
 *
 * ── WHY CANDIDATES ARE COLLAPSED BY DEFAULT
 *
 * Measured 2026-09-02 against clone/rathje: one simulation of the productive Provisionsart
 * `ap_rathje` produces 37 steps, and its K-10 step for the Basisprovision alone carries 91
 * candidates. Rendering all of them expanded turns the answer into a wall in which the winner is
 * invisible. The winner and the verdict therefore always stand in the step header; the full list is
 * one click away, complete, in the delivered order, paginated instead of truncated — no candidate
 * is ever dropped, because a dropped candidate is exactly the one the reader was looking for.
 *
 * ── COLOURS COME FROM THE BACKEND, NOT FROM `severity`
 *
 * `severity` is inert in this application: `assets/styles/primeng-v20-overrides.scss:6319` colours
 * EVERY `p-tag` unqualified on `--primary-800`, which beats PrimeNG's own `.p-tag-success` /
 * `-warn`. Measured at the running system on 2026-09-02, a success chip and three secondary chips
 * all rendered `rgb(26,129,174)`. The chips here therefore take the colour tokens the backend
 * already ships in `verdictInfo` / `kindInfo` / `stateInfo` (from `@UiEnumValueInfo`) as an INLINE
 * style — inline beats a stylesheet rule that carries no `!important`, so the mapping survives, and
 * this component makes no colour decision of its own. Same construction as the reconciliation
 * screens of bm (`bm-reconciliation-detail-panel.component.html:944`). The protected global file
 * was not touched.
 */
@Component({
    selector: 'cp-provision-simulation-protocol',
    templateUrl: './cp-provision-simulation-protocol.component.html',
    styleUrls: ['./cp-provision-simulation-protocol.component.scss'],
    standalone: false
})
export class CpProvisionSimulationProtocolComponent implements OnInit, OnChanges {

    initialized: boolean;

    /** The protocol, in the order the engine produced it. Never re-ordered. */
    @Input() steps: ProvisionSimulationStepDto[] = [];

    /** The entitlements — only to NAME the row a step belongs to. */
    @Input() entitlements: ProvisionSimulationEntitlementDto[] = [];

    /** The messages of the calculation chain, with the same codes a productive run writes. */
    @Input() messages: ProvisionSimulationMessageDto[] = [];

    /**
     * The state of the run. Decides ONE thing here: whether the "keine Regel hat zusätzliche
     * Berater abgeleitet" line may be shown — see {@link showCalculatedAgentsNote}.
     */
    @Input() state: ProvisionSimulationState | number | string;

    /**
     * Human-readable name per `entitlementRef`, e.g. `-1` -> "Basisprovision · Frank Wilberg".
     * Built from the delivered entitlements so a step can say which Provisionssatz it belongs to
     * without the reader having to match negative ids by eye.
     */
    entitlementLabels: Map<number, string> = new Map<number, string>();

    /** Indices of the steps whose candidate list is currently expanded. */
    expandedCandidateSteps: Set<number> = new Set<number>();

    /** Above this many candidates the table paginates rather than growing without end. */
    readonly candidatePageSize: number = 25;

    ngOnInit(): void {
        this.initComponent();
    }

    /** Builds the derived display state and opens the component for change detection. */
    initComponent(): void {
        this.refreshComponent();
        this.initialized = true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.initialized) {
            return;
        }
        this.refreshComponent();
    }

    /**
     * Rebuilds the derived display state.
     *
     * The expansion state is dropped on every new answer on purpose: step 12 of the previous
     * simulation is not step 12 of this one, and carrying the open state over would leave the
     * reader looking at an expanded list they never opened.
     */
    refreshComponent(): void {

        this.expandedCandidateSteps = new Set<number>();
        this.entitlementLabels = new Map<number, string>();

        for (const entitlement of this.entitlements ?? []) {

            if (entitlement?.entitlementRef == null) {
                continue;
            }

            const roleInfo: DtoEnumInfo = entitlement.agentRoleInfo;
            const role: string = provisionSimulationEnumLabel(roleInfo, entitlement.agentRole, ProvisionAgentRoleEnum);
            const parts: string[] = [entitlement.componentName, entitlement.provisionAgentName, role];

            this.entitlementLabels.set(entitlement.entitlementRef, parts.filter(part => !!part).join(' · '));
        }
    }

    /**
     * Name of the Provisionssatz a step belongs to, or `null` for a contract-wide step.
     *
     * A `entitlementRef` the entitlement list does not know still yields a visible fallback rather
     * than nothing: a step pointing at a row that is not in the answer is a real inconsistency and
     * must not disappear behind a blank.
     */
    entitlementLabelOf(step: ProvisionSimulationStepDto): string {

        if (step?.entitlementRef == null) {
            return null;
        }

        return this.entitlementLabels.get(step.entitlementRef) ?? ('Provisionssatz ' + step.entitlementRef);
    }

    /** Is the candidate list of this step currently open? */
    isCandidatesExpanded(index: number): boolean {
        return this.expandedCandidateSteps.has(index);
    }

    /** Opens or closes the candidate list of one step. */
    handleToggleCandidates(index: number): void {

        if (this.expandedCandidateSteps.has(index)) {
            this.expandedCandidateSteps.delete(index);
            return;
        }

        this.expandedCandidateSteps.add(index);
    }

    /** Opens every candidate list — for reading a whole protocol top to bottom. */
    handleExpandAllCandidates(): void {

        const expanded = new Set<number>();

        (this.steps ?? []).forEach((step, index) => {
            if (step?.candidates?.length) {
                expanded.add(index);
            }
        });

        this.expandedCandidateSteps = expanded;
    }

    /** Closes every candidate list. */
    handleCollapseAllCandidates(): void {
        this.expandedCandidateSteps = new Set<number>();
    }

    /** True while at least one step carries candidates — otherwise the two buttons say nothing. */
    get anyCandidates(): boolean {
        return (this.steps ?? []).some(step => !!step?.candidates?.length);
    }

    /**
     * The label of a step's disclosure button — the ONE line a reader gets while the step is still
     * collapsed.
     *
     * ── WHY THE BREAKDOWN BELONGS HERE
     *
     * A step with 91 candidates is, closed, only as good as this line. "91 Kandidaten" says almost
     * nothing; "91 · 1 Gewonnen · 90 Nicht geprüft" usually answers the question outright — nobody
     * had to be opened, and the one row that won is already accounted for. That is the ordered
     * product ("detaillierte Informationen wie alles berechnet wurde") delivered at the cheapest
     * possible moment.
     *
     * ── NOTHING IS COUNTED HERE
     *
     * The numbers and the captions both arrive finished in `step.verdictCounts` and are only
     * concatenated into a sentence — the same class of operation as putting "€" behind an amount.
     * The order is the delivered order (the evaluation order) and is never sorted; the entries are
     * never filtered, so the parts always add up to the total in brackets.
     *
     * ── THE TOTAL STAYS
     *
     * Even beside the breakdown, because otherwise a reader has to add two or three numbers to know
     * how long the list they are about to open is.
     *
     * ── DEFENSIVE BY CONSTRUCTION
     *
     * `verdictCounts` is absent on a backend older than 02.09.2026 — backend is deployed by hand,
     * frontend by CI, so there is always a window. Without the field this returns exactly the label
     * the component carried before it existed.
     */
    candidateToggleLabel(step: ProvisionSimulationStepDto, expanded: boolean): string {

        const action: string = expanded ? 'Kandidaten ausblenden' : 'Kandidaten in Prüfreihenfolge anzeigen';

        const total: number = step?.candidates?.length ?? 0;
        const breakdown: string = this.verdictBreakdown(step);

        return action + ' (' + total + ')' + (breakdown ? ' · ' + breakdown : '');
    }

    /**
     * "1 Gewonnen · 90 Nicht geprüft" — the delivered tallies joined, in the delivered order.
     *
     * Every entry is rendered, including a list with only ONE entry: "20 Dimension passt nicht"
     * beside a total of 20 is the statement that NOBODY won, which is precisely the case a reader
     * must not have to open the list for. Suppressing single-entry breakdowns as "redundant" would
     * hide exactly that.
     */
    verdictBreakdown(step: ProvisionSimulationStepDto): string {

        const counts: ProvisionSimulationVerdictCountDto[] = step?.verdictCounts ?? [];

        if (!counts.length) {
            return '';
        }

        return counts.map(entry => entry.count + ' ' + this.verdictLabel(entry)).join(' · ');
    }

    /**
     * Caption of one tally entry.
     *
     * Prefers the `verdictInfo` the server sends. The fallback is deliberately LOUD rather than
     * blank: a constant appended to the backend enum after this file was written would otherwise
     * silently shorten a line whose parts must add up to the total beside it. Same reasoning as
     * {@link provisionEnumLabelFallback}, which is reused here for the numeric wire form.
     */
    protected verdictLabel(entry: ProvisionSimulationVerdictCountDto): string {

        if (entry?.verdictInfo?.label) {
            return entry.verdictInfo.label;
        }

        // The wire sends the ordinal; a name is tolerated and then already readable enough.
        if (typeof entry?.verdict === 'string') {
            return entry.verdict;
        }

        return provisionEnumLabelFallback(ProvisionSimulationCandidateVerdict, entry?.verdict);
    }

    /**
     * German rendering of a figure value. Formatting only — see
     * {@link formatProvisionSimulationValue} for the line between formatting and calculating.
     */
    formatFigure(figure: ProvisionSimulationFigureDto): string {
        return formatProvisionSimulationValue(figure?.value, figure?.unit);
    }

    /** German rendering of a candidate's validity period, or a dash when it carries none. */
    validityOf(candidate: ProvisionSimulationCandidateDto): string {

        if (!candidate?.validFrom && !candidate?.validTo) {
            return '—';
        }

        const from: string = formatProvisionSimulationValue(candidate.validFrom);
        const to: string = formatProvisionSimulationValue(candidate.validTo);

        return from + ' – ' + to;
    }

    /** German caption of a candidate's axis (persönlich / Gruppe); empty when it carries none. */
    axisOf(candidate: ProvisionSimulationCandidateDto): string {
        return provisionSimulationEnumLabel(candidate?.axisInfo, candidate?.axis, ProvisionConfigurationAxis);
    }

    /** German caption of a message's severity — `severityInfo` first, the FE enum as fallback. */
    severityLabel(message: ProvisionSimulationMessageDto): string {
        return provisionSimulationEnumLabel(message?.severityInfo, message?.severity, ProvisionMessageSeverity);
    }

    /** Inline colour of a chip from the backend's own tokens — the shared rule, see the presentation helper. */
    chipStyle(backgroundColor: string, color: string): { [key: string]: string } {
        return provisionSimulationChipStyle(backgroundColor, color);
    }

    /**
     * Whether to state that no K-17 rule derived an additional agent.
     *
     * MEASURED: the service never emits a step of kind `CALCULATED_AGENTS` — verified against the
     * source and against a fully calculated run of `ap_rathje` on 2026-09-02, which produced the
     * kinds 1–11 and nothing else. `INPUT` and `BOOKINGS` are equally absent but have a second
     * carrier in the answer (`dimensions[]` + `keyDates[]`, and `entitlements[].bookings`); K-17
     * has none, so its absence would read as "this screen forgot the step".
     *
     * The line is shown ONLY after `COMPLETED`. After `NOT_ELIGIBLE`, `NO_EVENT` or an error the
     * chain never reaches K-17 at all, and claiming "no rule fired" would be an answer this run
     * does not have.
     */
    get showCalculatedAgentsNote(): boolean {
        return this.resolvedState === ProvisionSimulationState.COMPLETED;
    }

    /** The state as the FE enum constant — the shared rule, see the presentation helper. */
    protected get resolvedState(): ProvisionSimulationState {
        return provisionSimulationResolvedState(this.state);
    }

    /**
     * Module colour class of a message box, decided on the FE enum constant.
     *
     * An unknown or absent severity is styled neutrally rather than dropped, because a message
     * nobody styled is still a message.
     */
    messageSeverityClass(message: ProvisionSimulationMessageDto): string {

        const severity: ProvisionMessageSeverity = this.severityOf(message);

        if (severity === ProvisionMessageSeverity.ERROR) {
            return 'cp-sim-message--error';
        }
        if (severity === ProvisionMessageSeverity.WARNING) {
            return 'cp-sim-message--warning';
        }

        return 'cp-sim-message--info';
    }

    /**
     * The severity of a message as the FE enum constant, or `null` when it cannot be resolved.
     *
     * The wire sends the ordinal, which IS the constant. A backend older than that sends the enum
     * NAME, which is resolved through the enum mirror by name — the only place a raw string is
     * touched, and only to turn it into the constant every branch compares against.
     */
    protected severityOf(message: ProvisionSimulationMessageDto): ProvisionMessageSeverity {

        const severity: ProvisionMessageSeverity | number | string = message?.severity;

        if (typeof severity === 'number') {
            return severity;
        }

        if (typeof severity === 'string') {
            const ordinal: unknown = ProvisionMessageSeverity[severity as keyof typeof ProvisionMessageSeverity];
            return typeof ordinal === 'number' ? ordinal : null;
        }

        return null;
    }

}
